import { ARTICLE_STATUS } from "./articleConstants";
import { resolveArticleStatus } from "./articleStatusUtils";
import {
  buildArticlePayloadBySectionRules,
  getSectionNameFromOptions,
  getSectionRules,
  isFieldHidden,
} from "./articleSectionRulesUtils";

export const normalizeOptionalApiString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalized = String(value).trim();
  return normalized.toUpperCase() === "NA" ? "" : normalized;
};

export const normalizeArticleApiItem = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }

  const authorName = normalizeOptionalApiString(
    data.authorName ?? data.AuthorName
  );
  const sourceName = normalizeOptionalApiString(
    data.sourceName ?? data.SourceName
  );
  const eventLocation = normalizeOptionalApiString(
    data.eventLocation ?? data.EventLocation
  );
  const tagName = normalizeOptionalApiString(
    data.tagName ??
      data.TagName ??
      data.articleTagName ??
      data.ArticleTagName
  );
  const detailPageUrl = normalizeOptionalApiString(
    data.detailPageUrl ?? data.DetailPageUrl
  );
  const metaTitle = normalizeOptionalApiString(
    data.metaTitle ?? data.MetaTitle
  );
  const metaDescription = normalizeOptionalApiString(
    data.metaDescription ?? data.MetaDescription
  );
  const metaKeywords = normalizeOptionalApiString(
    data.metaKeywords ?? data.MetaKeywords
  );
  const readingTime = normalizeOptionalApiString(
    data.readingTime ?? data.ReadingTime
  );
  const publishedOn = data.publishedOn ?? data.PublishedOn ?? "";

  return {
    ...data,
    authorName,
    AuthorName: authorName,
    sourceName,
    SourceName: sourceName,
    eventLocation,
    EventLocation: eventLocation,
    tagName,
    TagName: tagName,
    articleTagName: tagName,
    ArticleTagName: tagName,
    detailPageUrl,
    DetailPageUrl: detailPageUrl,
    metaTitle,
    MetaTitle: metaTitle,
    metaDescription,
    MetaDescription: metaDescription,
    metaKeywords,
    MetaKeywords: metaKeywords,
    readingTime,
    ReadingTime: readingTime,
    publishedOn,
    PublishedOn: publishedOn,
  };
};

/**
 * Normalizes items from the Manage Articles listing response
 * (POST contents/articles-filter): id, title, contentSlug, contentImageUrl,
 * addedOn, publishedOn, status, isPublished, displayOrder. contentImageUrl
 * falls back to the older bannerImageUrl key for backward compatibility
 * during rollout. Kept separate from normalizeArticleApiItem, which still
 * backs the full-DTO Add/Edit Article endpoints (get/add/update) and must
 * not assume this trimmed shape.
 */
export const normalizeArticleListItem = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }

  return {
    id: data.id ?? data.Id,
    title: data.title || data.Title || "",
    contentSlug: data.contentSlug || data.ContentSlug || "",
    contentImageUrl:
      data.contentImageUrl ??
      data.ContentImageUrl ??
      data.bannerImageUrl ??
      data.BannerImageUrl ??
      "",
    addedOn: data.addedOn ?? data.AddedOn ?? null,
    publishedOn: data.publishedOn ?? data.PublishedOn ?? null,
    status: data.status ?? data.Status ?? "",
    isPublished: Boolean(data.isPublished ?? data.IsPublished ?? false),
    displayOrder: data.displayOrder ?? data.DisplayOrder ?? null,
  };
};

const normalizeOptionalPayloadString = (value) =>
  normalizeOptionalApiString(value);

const appendPayloadValue = (payload, key, value) => {
  if (value instanceof File) {
    payload.append(key, value);
    return;
  }
  if (value === undefined || value === null) {
    payload.append(key, "");
    return;
  }
  payload.append(key, String(value));
};

 const OPTIONAL_TEXT_SENTINEL = "NA";

const appendOptionalTextField = (payload, camelKey, pascalKey, value) => {
  const normalized = normalizeOptionalPayloadString(value);
  const safeValue = normalized || OPTIONAL_TEXT_SENTINEL;
  appendPayloadValue(payload, camelKey, safeValue);
  appendPayloadValue(payload, pascalKey, safeValue);
};

// PublishedOn is backend-required (non-nullable DateTime) — if left blank,
// default to "now" (publish immediately), which is the correct UX meaning
// of an unset publish date.
const appendPublishedOnField = (payload, value) => {
  const normalized = normalizeOptionalPayloadString(value);
  const safeValue = normalized || new Date().toISOString();
  payload.append("publishedOn", safeValue);
  payload.append("PublishedOn", safeValue);
};

// EventsFrom / EventsTo are only meaningful when the Event Settings block
// is visible. When hidden/blank, the backend still requires *a* value
// (non-nullable DateTime), so we send a neutral placeholder rather than
// omitting the field. Flagging: confirm with backend that this placeholder
// is safe/ignored, or ideally get these columns made nullable.
const NEUTRAL_EVENT_DATE = "1900-01-01T00:00:00";

const appendOptionalDateField = (payload, camelKey, pascalKey, value) => {
  const normalized = normalizeOptionalPayloadString(value);
  const safeValue = normalized || NEUTRAL_EVENT_DATE;
  payload.append(camelKey, safeValue);
  payload.append(pascalKey, safeValue);
};
/**
 * Builds multipart payload for articles API.
 */
export const buildArticleFormData = (
  formData,
  isEditMode,
  id,
  status,
  sectionRules = null,
  selectedSectionName = ""
) => {
  const sanitized = buildArticlePayloadBySectionRules(
    formData,
    selectedSectionName
  );

  const rules =
    sectionRules ||
    getSectionRules(selectedSectionName) ||
    { hiddenFields: [], requiredFields: [] };
  const payload = new FormData();

  if (isEditMode && id) {
    payload.append("id", id);
    payload.append("Id", id);
  }

  payload.append("sectionId", sanitized.sectionId);

  appendPayloadValue(payload, "subSectionId", sanitized.subSectionId);
  appendPayloadValue(payload, "ArticleTag", sanitized.articleTag || "");

  payload.append("title", sanitized.title);
  payload.append("contentSlug", sanitized.contentSlug);
  appendPayloadValue(payload, "shortDescription", sanitized.shortDescription);
  payload.append("fullDescription", sanitized.fullDescription || "");

  if (!isFieldHidden("contentImage", rules) && sanitized.contentImageUrl) {
    payload.append("contentImageUrl", String(sanitized.contentImageUrl));
  }
  if (!isFieldHidden("bannerImage", rules) && sanitized.bannerImageUrl) {
    payload.append("bannerImageUrl", String(sanitized.bannerImageUrl));
  }

  // Detail Page URL is no longer user-editable; always send the sentinel.
  appendOptionalTextField(payload, "detailPageUrl", "DetailPageUrl", "NA");
  appendOptionalTextField(
    payload,
    "authorName",
    "AuthorName",
    sanitized.authorName
  );
  appendOptionalTextField(
    payload,
    "sourceName",
    "SourceName",
    sanitized.sourceName
  );
  appendOptionalTextField(
    payload,
    "readingTime",
    "ReadingTime",
    sanitized.readingTime
  );
  appendOptionalDateField(
    payload,
    "publishedOn",
    "PublishedOn",
    sanitized.publishedOn
  );

  appendOptionalDateField(
    payload,
    "eventsFrom",
    "EventsFrom",
    sanitized.eventsFrom
  );
  appendOptionalDateField(payload, "eventsTo", "EventsTo", sanitized.eventsTo);
  appendOptionalTextField(
    payload,
    "eventLocation",
    "EventLocation",
    sanitized.eventLocation
  );

  payload.append("isFeatured", String(sanitized.isFeatured));
  payload.append("featuredHome", String(sanitized.isFeaturedHome));
  payload.append("isPremium", String(sanitized.isPremium));
  appendPayloadValue(payload, "displayOrder", sanitized.displayOrder);

  appendOptionalTextField(payload, "metaTitle", "MetaTitle", sanitized.metaTitle);
  appendOptionalTextField(
    payload,
    "metaDescription",
    "MetaDescription",
    sanitized.metaDescription
  );
  appendOptionalTextField(
    payload,
    "metaKeywords",
    "MetaKeywords",
    sanitized.metaKeywords
  );

  const resolvedStatus = status || ARTICLE_STATUS.DRAFT;
  payload.append("Status", resolvedStatus);
  payload.append("status", resolvedStatus);

  return payload;
};

/**
 * Resolves section label and builds FormData in one step (add + update).
 */
export const buildArticleFormDataForSubmit = (
  formData,
  isEditMode,
  id,
  status,
  sectionOptions = []
) => {
  const selectedSectionName = getSectionNameFromOptions(
    formData.sectionId,
    sectionOptions
  );
  const sectionRules = getSectionRules(selectedSectionName);

  return buildArticleFormData(
    formData,
    isEditMode,
    id,
    status,
    sectionRules,
    selectedSectionName
  );
};

export const mapArticleResponse = (data) => {
  const normalized = normalizeArticleApiItem(data);

  return {
    sectionId: String(normalized.sectionId ?? normalized.SectionId ?? ""),
    subSectionId: String(normalized.subSectionId ?? normalized.SubSectionId ?? ""),
    articleTag: String(
      normalized.articleTag ?? normalized.ArticleTag ?? ""
    ),
    title: normalized.title || normalized.Title || "",
    contentSlug: normalized.contentSlug || normalized.ContentSlug || "",
    shortDescription:
      normalized.shortDescription || normalized.ShortDescription || "",
    fullDescription:
      normalized.fullDescription || normalized.FullDescription || "",
    detailPageUrl: normalized.detailPageUrl,
    authorName: normalized.authorName,
    sourceName: normalized.sourceName,
    readingTime: normalized.readingTime,
    publishedOn: normalized.publishedOn,
    eventsFrom: normalized.eventsFrom || normalized.EventsFrom || "",
    eventsTo: normalized.eventsTo || normalized.EventsTo || "",
    eventLocation: normalized.eventLocation,
    tagName: normalized.tagName,
    isFeatured: Boolean(normalized.isFeatured ?? normalized.IsFeatured ?? false),
    isFeaturedHome: Boolean(
      normalized.featuredHome ??
        normalized.FeaturedHome ??
        normalized.isFeaturedHome ??
        normalized.IsFeaturedHome ??
        false
    ),
    isPremium: Boolean(normalized.isPremium ?? normalized.IsPremium ?? false),
    displayOrder:
      normalized.displayOrder !== undefined && normalized.displayOrder !== null
        ? String(normalized.displayOrder)
        : normalized.DisplayOrder !== undefined &&
            normalized.DisplayOrder !== null
          ? String(normalized.DisplayOrder)
          : "",
    metaTitle: normalized.metaTitle,
    metaDescription: normalized.metaDescription,
    metaKeywords: normalized.metaKeywords,
    status: resolveArticleStatus(normalized),
    contentImageUrl:
      normalized.contentImageUrl ||
      normalized.ContentImageUrl ||
      normalized.thumbnailUrl ||
      normalized.ThumbnailUrl ||
      "",
    bannerImageUrl:
      normalized.bannerImageUrl || normalized.BannerImageUrl || "",
    contentImagePreview:
      normalized.contentImageUrl ||
      normalized.ContentImageUrl ||
      normalized.thumbnailUrl ||
      normalized.ThumbnailUrl ||
      "",
    bannerImagePreview:
      normalized.bannerImageUrl || normalized.BannerImageUrl || "",
    contentImageFile: null,
    bannerImageFile: null,
  };
};
